// src/pages/PortfolioManagement.js
import React, { useState } from 'react';
import { usePortfolio } from '../contexts/PortfolioContext';
import { useCurrency, CURRENCY_FLAGS } from '../contexts/CurrencyContext';
import { doc, setDoc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../services/firebase/config';
import { toast } from 'react-hot-toast';
import { Trash2, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import IconSelector from '../components/portfolio/IconSelector';

const PortfolioManagement = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [notes, setNotes] = useState('');
  const [icon, setIcon] = useState('💼');
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [editIcon, setEditIcon] = useState('💼');
  const { selectedCurrency, setSelectedCurrency } = useCurrency();
  const { portfolios, refreshPortfolios, loading } = usePortfolio();

  const CURRENCIES = Object.entries(CURRENCY_FLAGS).map(([code, data]) => ({
    code,
    name: data.name
  }));

  if (!auth.currentUser?.email) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4 text-gray-900 dark:text-white">
        <div className="text-center">Lütfen önce giriş yapınız.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4 text-gray-900 dark:text-white">
        <div className="text-center">Portföyleriniz yükleniyor...</div>
      </div>
    );
  }

  if (!portfolios || portfolios.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4 text-gray-900 dark:text-white">
        <div className="text-center">Henüz portföy bulunmamaktadır. Yeni bir portföy oluşturabilirsiniz.</div>
      </div>
    );
  }

  const resetForm = () => {
    setName('');
    setGoal('');
    setNotes('');
    setIcon('💼');
    setEditIcon('💼');
    setEditingPortfolio(null);
    setShowCreateForm(false);
  };

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    if (!name || !auth.currentUser?.email) return;

    try {
      const portfolioId = name.trim()
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/\s+/g, '-');
      const portfolioRef = doc(db, 'users', auth.currentUser.email, 'portfolios', portfolioId);
      
      const existingPortfolio = await getDoc(portfolioRef);
      if (existingPortfolio.exists()) {
        toast.error('Bu isimde bir portföy zaten var');
        return;
      }

      await setDoc(portfolioRef, {
        id: portfolioId,
        name: name.trim(),
        goal: parseFloat(goal) || 0,
        currency: selectedCurrency,
        notes: notes.trim(),
        createdAt: new Date(),
        isDefault: false,
        icon
      });
      
      await refreshPortfolios();
      resetForm();
      toast.success('Portföy başarıyla oluşturuldu');
    } catch (error) {
      toast.error('Portföy oluşturulurken hata oluştu');
    }
  };

  const handleDelete = async (portfolioId) => {
    if (!window.confirm('Bu portföyü silmek istediğinizden emin misiniz?')) return;
    if (portfolioId === 'default') return;

    try {
      await deleteDoc(doc(db, 'users', auth.currentUser.email, 'portfolios', portfolioId));
      await refreshPortfolios();
      toast.success('Portföy başarıyla silindi');
    } catch (error) {
      toast.error('Portföy silinirken hata oluştu');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault(); // Form submit'i engelle
    if (!editingPortfolio) return;

    try {
      const portfolioRef = doc(db, 'users', auth.currentUser.email, 'portfolios', editingPortfolio.id);
      await updateDoc(portfolioRef, {
        name: name.trim(),
        goal: parseFloat(goal) || 0,
        notes: notes.trim(),
        icon: editIcon
      });
      
      await refreshPortfolios();
      toast.success('Portföy güncellendi');
      resetForm();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const startEditing = (portfolio) => {
    setEditingPortfolio(portfolio);
    setName(portfolio.name);
    setGoal(portfolio.goal?.toString() || '');
    setNotes(portfolio.notes || '');
    setEditIcon(portfolio.icon || '💼');
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 text-gray-900 dark:text-white">
      <button
        onClick={() => setShowCreateForm(!showCreateForm)}
        className="w-full mb-4 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex justify-between items-center px-4"
      >
        <span>Yeni Portföy Oluştur</span>
        {showCreateForm ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {showCreateForm && (
        <div className="bg-white dark:bg-[#1f2544] rounded-lg p-6 mb-8 shadow-lg">
          <form onSubmit={handleCreatePortfolio} className="space-y-4">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">İkon</label>
                <IconSelector currentIcon={icon} onSelect={setIcon} />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">Portföy İsmi</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: Emeklilik Portföyü"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Hedef</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  className="flex-1 p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: 1,000,000"
                />
                <select 
                  className="w-32 p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  {CURRENCIES.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notlar</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-3 bg-gray-50 dark:bg-[#151934] rounded-md border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white min-h-[100px]"
                placeholder="Portföy hakkında notlar..."
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Portföy Oluştur
            </button>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-[#1f2544] rounded-lg p-6 shadow-lg">
        <h2 className="text-xl font-semibold mb-6">Portföylerim</h2>
        <div className="space-y-4">
          {portfolios.map(portfolio => (
            <div key={portfolio.id} className="bg-gray-50 dark:bg-[#151934] rounded-md">
              {editingPortfolio?.id === portfolio.id ? (
                <form onSubmit={handleEdit} className="p-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">İkon</label>
                      <IconSelector 
                        currentIcon={editIcon} 
                        onSelect={(selectedIcon) => {
                          setEditIcon(selectedIcon);
                        }} 
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-2">İsim</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full p-2 bg-gray-50 dark:bg-[#1f2544] rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Hedef</label>
                    <input
                      type="number"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      className="w-full p-2 bg-gray-50 dark:bg-[#1f2544] rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Notlar</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full p-2 bg-gray-50 dark:bg-[#1f2544] rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setEditingPortfolio(null)}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Güncelle
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between p-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{portfolio.icon || '💼'}</span>
                      <h3 className="font-medium">{portfolio.name}</h3>
                    </div>
                    {portfolio.goal && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Hedef: {portfolio.goal.toLocaleString()} {portfolio.currency || selectedCurrency}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => startEditing(portfolio)} 
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <Edit2 size={18} />
                    </button>
                    {portfolio.id !== 'default' && (
                      <button 
                        onClick={() => handleDelete(portfolio.id)} 
                        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioManagement;